import { useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import {
  LIVE_LEDGER_ACCOUNT_QUERY_KEY,
  LIVE_LEDGER_DATE_FROM_QUERY_KEY,
  LIVE_LEDGER_DATE_TO_QUERY_KEY,
  LIVE_LEDGER_SEARCH_QUERY_KEY,
  TRANSACTION_SUMMARY_QUERY_KEY,
} from "../constants";
import { useFilterContext } from "../context/FilterContext";
import { hasFutureDate } from "../helpers";

export interface TransactionSummary {
  money_in: number;
  money_out: number;
  net_change: number;
}

export interface UseTransactionSummaryResult {
  summary: TransactionSummary | null;
  isFiltered: boolean;
  error: string | null;
  isLoading: boolean;
}

const DEFAULT_RANGE_DAYS = 30;

const toStartOfDay = (date: string): string =>
  date.includes("T") ? date : `${date}T00:00:00`;

const toEndOfDay = (date: string): string =>
  date.includes("T") ? date : `${date}T23:59:59`;

const toLocalYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

interface RequestConfig {
  endpoint: string;
  params: Record<string, string>;
}

const buildRequest = (
  filterState: Record<string, string>,
  hasAnyActive: boolean
): RequestConfig => {
  const params: Record<string, string> = { filter: "hidden" };
  let endpoint = "transactions/summary";

  if (hasAnyActive) {
    const {
      [LIVE_LEDGER_ACCOUNT_QUERY_KEY]: accountIds,
      [LIVE_LEDGER_SEARCH_QUERY_KEY]: search,
      [LIVE_LEDGER_DATE_FROM_QUERY_KEY]: settledFrom,
      [LIVE_LEDGER_DATE_TO_QUERY_KEY]: settledTo,
      ...rest
    } = filterState;

    Object.assign(params, rest);
    if (search) params.q = search;
    if (settledFrom) params.settled_from = toStartOfDay(settledFrom);
    if (settledTo) params.settled_to = toEndOfDay(settledTo);
    if (accountIds) {
      endpoint = `accounts/${accountIds}/transactions/summary`;
    }
  } else {
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
    params.settled_from = toStartOfDay(toLocalYmd(from));
    params.settled_to = toEndOfDay(toLocalYmd(now));
  }

  return { endpoint, params };
};

const extractErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return null;
};

export const useTransactionSummary = (
  enabled = true,
  includeInternalTransfers = false
): UseTransactionSummaryResult => {
  const { filterState, hasAnyActive } = useFilterContext();
  const { endpoint, params } = buildRequest(filterState, hasAnyActive);
  if (!includeInternalTransfers) {
    params.exclude_internal_transfers = "true";
  }

  const hasFutureDates = hasFutureDate(
    filterState[LIVE_LEDGER_DATE_FROM_QUERY_KEY],
    filterState[LIVE_LEDGER_DATE_TO_QUERY_KEY]
  );

  const { data, error, isLoading } = useQuery<TransactionSummary>({
    queryKey: [TRANSACTION_SUMMARY_QUERY_KEY, endpoint, params],
    queryFn: () => ApiHttp.fetch<TransactionSummary>(endpoint, {}, params),
    enabled: enabled && !hasFutureDates,
  });

  return {
    summary: data ?? null,
    isFiltered: hasAnyActive,
    error: extractErrorMessage(error),
    isLoading: enabled && isLoading,
  };
};
